import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaCalendar, FaTrophy, FaHandsHelping, FaChalkboardTeacher, FaMicrophoneAlt } from 'react-icons/fa';
import { experiences } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { useTranslation } from 'react-i18next';
import { localizedItem } from '../locales/contentTranslations';
import useCardSlider from "../hooks/useCardSlider";


const Experience = () => {
  const { t, i18n } = useTranslation();
  const [activeType, setActiveType] = useState('all');
  const [selectedExpId, setSelectedExpId] = useState(null);
  const selectedExp = experiences.map(exp => localizedItem('experiences', exp, i18n)).find(exp => exp.id === selectedExpId);
  const filteredExperiences = (activeType === 'all'
    ? experiences
    : experiences.filter(exp => exp.type === activeType)
  ).map(exp => localizedItem('experiences', exp, i18n));
  const {
    startIndex,
    setStartIndex,
    visibleCount,
    next: nextSlide,
    prev: prevSlide,
    reset,
    sliderProps
  } = useCardSlider(
    filteredExperiences.length,
    selectedExpId !== null,
    10000,
    true
  );

  const totalPages = Math.ceil(filteredExperiences.length / visibleCount);
  const currentPage = Math.floor(startIndex / visibleCount);

  const visibleExperiences = filteredExperiences.slice(
    startIndex,
    startIndex + visibleCount
  );

  const getTypeIcon = (type) => {
    const icons = {
      mentorship: { icon: FaChalkboardTeacher, color: "#10B981", label: t("experience.mentorship") },
      volunteering: { icon: FaHandsHelping, color: "#F59E0B", label: t("experience.volunteering") },
      event: { icon: FaMicrophoneAlt, color: "#8B5CF6", label: t("experience.events") },
      education: { icon: FaChalkboardTeacher, color: "#3B82F6", label: t("education.title") }
    };
    return icons[type] || { icon: FaTrophy, color: "#6366F1", label: t("experience.other") };
  };

  const openModal = (exp) => {
    setSelectedExpId(exp.id);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedExpId(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <section id="experience" className="py-16 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {t("experience.title1")} <span className="text-gradient">{t("experience.title2")}</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t("experience.subtitle")}
            </p>
          </div>
        </AnimatedSection>

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {[
            { id: 'all', label: t('experience.all'), icon: '🎯' },
            { id: 'mentorship', label: t('experience.mentorship'), icon: '👨‍🏫' },
            { id: 'volunteering', label: t('experience.volunteering'), icon: '🤝' },
            { id: 'event', label: t('experience.events'), icon: '🎤' }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => { setActiveType(filter.id); reset(); }}
              className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${
                activeType === filter.id 
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg' 
                  : 'glass-effect text-gray-300 hover:text-white'
              }`}
            >
              <span>{filter.icon}</span>
              <span>{filter.label}</span>
            </button>
          ))}
        </div>

        {/* Slajder */}
        <div className="relative" {...sliderProps}>
          {filteredExperiences.length > visibleCount && (
            <>
              <button 
                onClick={prevSlide} 
                className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                <FaChevronLeft />
              </button>
              <button 
                onClick={nextSlide} 
                className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                <FaChevronRight />
              </button>
            </>
          )}

          <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-sm mx-auto md:max-w-none">
            <AnimatePresence mode="wait">
              {visibleExperiences.map((exp, idx) => {
                const { icon: TypeIcon, color, label } = getTypeIcon(exp.type);
                return (
                  <motion.div
                    key={exp.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -8 }}
                    onClick={() => openModal(exp)}
                    className="group cursor-pointer min-w-0 h-full"
                  >
                    <div className="glass-effect rounded-2xl overflow-hidden h-full transition-all hover-glow">
                      {/* SLIKA - Ako postoji backgroundImage */}
                      {exp.backgroundImage && (
                        <div className="relative h-36 overflow-hidden">
                          <img
                            src={exp.backgroundImage}
                            alt={exp.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent"></div>
                        </div>
                      )}
                      
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: `${color}20` }}>
                              {exp.icon}
                            </div>
                            <div>
                              <h3 className="font-semibold text-white group-hover:text-primary transition line-clamp-1">
                                {exp.title}
                              </h3>
                              <p className="text-sm text-gray-400">{exp.organization}</p>
                            </div>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full whitespace-nowrap" style={{ background: color, color: '#fff' }}>
                            {label}
                          </span>
                        </div>
                        
                        <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                          {exp.description}
                        </p>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <div className="flex items-center gap-1">
                            <FaCalendar size={10} />
                            <span>{exp.year}</span>
                          </div>
                          {exp.location && (
                            <div className="flex items-center gap-1">
                              <FaMapMarkerAlt size={10} />
                              <span className="truncate">{exp.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            </div>
          </div>

          {/* Paginacija */}
          {filteredExperiences.length > visibleCount && (
            <div className="flex justify-center gap-1 mt-3">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setStartIndex(idx * visibleCount)}
                  className={`transition-all ${
                    currentPage === idx 
                      ? 'w-3 h-0.5 bg-primary rounded-full' 
                      : 'w-1 h-0.5 bg-gray-600/40 rounded-full'
                  }`}
                />
              ))}
            </div>
          )}
          {filteredExperiences.length > 0 && (
            <div className="text-center mt-4">
              <p className="text-gray-500 text-sm">
                {t('experience.showing', { defaultValue: 'Showing' })} {startIndex + 1} - {Math.min(startIndex + visibleCount, filteredExperiences.length)} {t('experience.of', { defaultValue: 'of' })} {filteredExperiences.length} {t('experience.items', { defaultValue: 'items' })}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal za detalje */}
      <AnimatePresence>
        {selectedExp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-gradient-to-br from-darker to-dark rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={closeModal} 
                className="absolute top-4 right-4 z-10 w-8 h-8 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                ✕
              </button>
              
              {/* Slika u modalu */}
              {selectedExp.backgroundImage && (
                <div className="relative h-48 overflow-hidden rounded-t-2xl">
                  <img
                    src={selectedExp.backgroundImage}
                    alt={selectedExp.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent"></div>
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl bg-primary/20">
                    {selectedExp.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedExp.title}</h3>
                    <p className="text-primary text-sm">{selectedExp.organization}</p>
                  </div>
                </div>
                
                <div className="flex gap-3 text-sm text-gray-400 mb-4">
                  <div className="flex items-center gap-1">
                    <FaCalendar size={12} /> {selectedExp.year}
                  </div>
                  {selectedExp.location && (
                    <div className="flex items-center gap-1">
                      <FaMapMarkerAlt size={12} /> {selectedExp.location}
                    </div>
                  )}
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">{selectedExp.description}</p>
                
                {selectedExp.achievements && selectedExp.achievements.length > 0 && (
                  <div className="pt-3 border-t border-white/10">
                    <h4 className="font-semibold text-sm mb-2 text-primary">{t('experience.achievements')}:</h4>
                    <ul className="space-y-1">
                      {selectedExp.achievements.map((ach, i) => (
                        <li key={i} className="text-sm text-gray-400 flex items-center gap-2">
                          <FaTrophy size={12} className="text-yellow-500" /> {ach}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Experience;